import React, { useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { signup } from "../../Services/authService";
import { useNavigate, Link } from "react-router-dom";
import toast from "react-hot-toast";
import { MESSAGES, SIGNUP_FIELDS } from "./Constants";
import { COMPANY_NAME, ROLES } from "../../Constants/Strings";
import { FloatingInput, Spinner, PinInput } from "../../Components/Common";
import { LOGIN } from "../../routes";
import { SilverLine_Logo, silverline_image } from "../../Assets";

const validationSchema = Yup.object().shape({
  first_name: Yup.string().required(MESSAGES.REQUIRED),
  last_name: Yup.string().required(MESSAGES.REQUIRED),
  email: Yup.string().email(MESSAGES.INVALID_MAIL).required(MESSAGES.REQUIRED),
  password: Yup.string()
    .matches(/^[0-9]+$/, MESSAGES.ONLY_DIGITS)
    .max(4, MESSAGES.PIN_VALIDATION)
    .required(MESSAGES.REQUIRED),
  confirmPassword: Yup.string()
    .matches(/^[0-9]+$/, MESSAGES.ONLY_DIGITS)
    .oneOf([Yup.ref("password"), null], MESSAGES.PIN_MATCH)
    .required(MESSAGES.REQUIRED),
  mobile: Yup.string()
    .matches(/^[0-9]+$/, MESSAGES.ONLY_DIGITS)
    .min(10, MESSAGES.MOBILE_VALIDATION)
    .required(MESSAGES.REQUIRED),
});

const SignUpForm = () => {
  const [showLogin, setShowLogin] = useState(true);
  const navigate = useNavigate();
  const initialValues = {
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    confirmPassword: "",
    role: "",
    mobile: "",
  };

  const onSubmit = async (values, { setSubmitting }) => {
    try {
      const { confirmPassword, ...payload } = values;
      const response = await signup({ ...payload, role: ROLES.EMPLOYEE });

      if (response.status === 201) {
        toast.success(MESSAGES.SIGNUP_SUCCESS);
        setTimeout(() => navigate(LOGIN), 3000);
      } else {
        toast.error(MESSAGES.SIGNUP_FAILED);
      }
    } catch (error) {
      toast.error(MESSAGES.SIGNUP_ERROR);
    } finally {
      setSubmitting(false);
      setShowLogin(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen w-full relative">
      {/* Responsive background image using Tailwind */}
      <div
        className="absolute inset-0 bg-cover bg-center z-0"
        style={{
          backgroundImage: `url(${silverline_image})`,
        }}
      />
      <div
        className="absolute inset-0 bg-cover bg-center z-0 hidden md:block"
        style={{
          backgroundImage: `url(${silverline_image})`,
        }}
      />

      {/* Form container */}
      <div className="w-full max-w-md bg-white/90 backdrop-blur-md p-6 rounded-md shadow-md relative z-10 m-4">
        {/* Logo + Title */}
        <div className="flex items-center justify-center gap-4 mb-4">
          <img
            src={SilverLine_Logo}
            alt="SilverLine Logo"
            className="w-10 h-10 object-contain"
          />
          <h1 className="text-4xl font-semibold text-blue-500">
            {COMPANY_NAME}
          </h1>
        </div>

        <h2 className="text-center text-xl font-bold text-primary mb-4">
          Sign Up
        </h2>

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
        >
          {({ isSubmitting }) => (
            <Form className="space-y-3">
              {SIGNUP_FIELDS.map((field) => (
                <FloatingInput
                  key={field.name}
                  label={field.label}
                  name={field.name}
                  type={field.type}
                  css={field.css}
                  maxLength={field.maxLength}
                  disabled={isSubmitting}
                />
              ))}

              <div className="flex flex-col md:flex-row gap-3">
                <PinInput name="password" label="Pin" />
                <PinInput name="confirmPassword" label="Confirm Pin" />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="flex justify-center w-full bg-primary hover:bg-hoverColor text-white font-semibold py-2 rounded mt-4"
              >
                {!isSubmitting ? "Sign Up" : <Spinner />}
              </button>
              {showLogin ? (
                ""
              ) : (
                <p className="text-primary text-center">
                  {MESSAGES.AFTER_REGISTRATION_MSG}{" "}
                </p>
              )}
            </Form>
          )}
        </Formik>

        {showLogin ? (
          <p className="text-center text-sm text-gray-600 mt-3">
            Already a member?{" "}
            <Link
              to={LOGIN}
              className="font-semibold text-gray-600 hover:underline"
            >
              Login Now!
            </Link>
          </p>
        ) : (
          ""
        )}
      </div>
    </div>
  );
};

export default SignUpForm;
